import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { BuilderUserContext } from '@gh/builder-shared';
import { BuilderPermissionSet, ContactType } from '@gh/builder-shared-data';
import { AppRouter, EnumDictionary, getRouteInitData, DictionaryService, globalDictionaries } from '@gh/common-shared';
import { SlicedDataSource, SortDirection } from '@gh/core-data';
import { DialogService, NotificationService, confirmationWith, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { SelectionModel } from '@angular/cdk/collections';
import { groupBy, map, sortBy, toPairs, assign } from 'lodash';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ContactListBusinessService } from '../../resources/contact-list-business.service';
import { ContactListItem } from '../../resources/contact-list-item.model';
import { ContactGridDefinition } from '../contact-list/contact-grid-definition.service';
import { ContactListFormState } from '../contact-list/contact-list-form.service';
import { ContactListInit } from '../../resources/contact-list-init.model';
import { ContactMergeDialogComponent } from '../contact-merge-dialog/contact-merge-dialog.component';

@Component({
  standalone: false,
  selector: 'gh-contact-merge-list',
  templateUrl: 'contact-merge-list.component.html',
  styleUrls: ['contact-merge-list.component.scss'],
  viewProviders: [
    ContactListFormState,
    ContactGridDefinition,
  ],
})
export class ContactMergeListComponent implements OnInit, OnDestroy {
  dataSource: SlicedDataSource<ContactListItem>;
  init: ContactListInit;
  groups: { name: string; primaryId: string; matchReasons?: string; items: ContactListItem[] }[] = [];
  loading = false;
  selections: { [primaryId: string]: SelectionModel<ContactListItem> } = {};
  contactTypes: EnumDictionary<ContactType>;
  totalCount = 0;
  searchQuery = '';
  displayedColumns: string[] = ['select', 'name', 'firstName', 'lastName', 'addressee', 'businessName', 'email', 'otherEmails', 'phone1', 'phone2', 'types', 'status', 'role', 'actions'];

  private readonly destroy$ = new Subject<void>();

  constructor(private route: ActivatedRoute,
              private dialog: MatDialog,
              private dialogService: DialogService,
              private userContext: BuilderUserContext,
              public form: ContactListFormState,
              private businessService: ContactListBusinessService,
              private router: AppRouter,
              public gridDefinition: ContactGridDefinition,
              private dictionaryService: DictionaryService,
              private notificationService: NotificationService) {

  }

  ngOnInit(): void {
    const { gridLayout, ...init } = getRouteInitData(this.route);
    assign(this, init);
    this.gridDefinition.bind(gridLayout);

    // Preload dictionaries to avoid slow loading when opening the Compare dialog for the first time
    forkJoin({
      gender: this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.gender),
      paidBy: this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.paidBy),
      payBasis: this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.payBasis),
      employeePayroll: this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.employeePayroll),
    }).pipe(takeUntil(this.destroy$)).subscribe();

    this.form.build({ types: [], showInactive: true }, this.init?.options || []);
    this.loadDuplicates();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onMerge(group: { name: string; primaryId: string; matchReasons?: string; items: ContactListItem[] }): void {
    if (!this.isCompareEnabled(group.primaryId)) {
      return;
    }
    const selection = this.selections[group.primaryId];
    if (!selection) {
      return;
    }
    const selected = selection.selected;
    let primary = selected.find(i => i.primary);
    let duplicate = selected.find(i => !i.primary);

    if (!primary || !duplicate) {
      primary = selected[0];
      duplicate = selected[1];
    }

    const dialogRef = this.dialog.open(ContactMergeDialogComponent, {
      width: '90vw',
      maxWidth: '1400px',
      minWidth: '1000px',
      disableClose: true,
      panelClass: 'contact-merge-dialog-panel',
      data: {
        primaryContactId: primary.id,
        duplicateContactId: duplicate.id,
        primaryHasXeroContact: primary.hasXeroContact,
        duplicateHasXeroContact: duplicate.hasXeroContact
      }
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result) {
        this.loadDuplicates();
      }
    });
  }

  onDismissPairRow(group: { name: string; primaryId: string; matchReasons?: string; items: ContactListItem[] }, duplicateItem: ContactListItem): void {
    const primaryItem = group.items.find(i => i.primary) || group.items[0];
    if (!primaryItem || !duplicateItem || primaryItem.id === duplicateItem.id) {
      return;
    }

    this.dialogService.confirmation(
      yesNoConfirmation('msg_dismiss_duplicate_confirm')
    ).pipe(takeUntil(this.destroy$)).subscribe(confirm => {
      if (yesAnswer(confirm)) {
        this.businessService.dismissDuplicate(primaryItem.id, duplicateItem.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              // Remove the dismissed duplicate row from this group
              group.items = group.items.filter(item => item.id !== duplicateItem.id);
              if (this.selections[group.primaryId]) {
                this.selections[group.primaryId].deselect(duplicateItem);
              }

              // If less than 2 items remain in the group, remove the entire group card
              if (group.items.length < 2) {
                this.groups = this.groups.filter(g => g.primaryId !== group.primaryId);
              }

              if (this.totalCount > 0) {
                this.totalCount--;
              }
            },
            error: (err) => this.notificationService.processAll(err)
          });
      }
    });
  }

  get filteredGroups(): { name: string; primaryId: string; matchReasons?: string; items: ContactListItem[] }[] {
    if (!this.searchQuery || !this.searchQuery.trim()) {
      return this.groups;
    }
    const query = this.searchQuery.trim().toLowerCase();
    return this.groups.filter(group => {
      if (group.name && group.name.toLowerCase().includes(query)) {
        return true;
      }
      if (group.matchReasons && group.matchReasons.toLowerCase().includes(query)) {
        return true;
      }
      return group.items.some(item => {
        const name = (item.name || '').toLowerCase();
        const firstName = (item.firstName || '').toLowerCase();
        const lastName = (item.lastName || '').toLowerCase();
        const addressee = (item.addressee || '').toLowerCase();
        const businessName = (item.businessName || '').toLowerCase();
        const email = (item.email || '').toLowerCase();
        const otherEmails = (item.otherEmails || '').toLowerCase();
        const phone1 = (item.phone1 || '').toLowerCase();
        const phone2 = (item.phone2 || '').toLowerCase();
        const phones = (item.phones || '').toLowerCase();
        const types = this.getFormattedTypes(item).toLowerCase();

        return name.includes(query) ||
               firstName.includes(query) ||
               lastName.includes(query) ||
               addressee.includes(query) ||
               businessName.includes(query) ||
               email.includes(query) ||
               otherEmails.includes(query) ||
               phone1.includes(query) ||
               phone2.includes(query) ||
               phones.includes(query) ||
               types.includes(query);
      });
    });
  }

  clearSearch(): void {
    this.searchQuery = '';
  }

  loadDuplicates(): void {
    this.loading = true;
    this.dataSource = this.businessService.findAllDuplicates(this.form.getValue(), this.init);

    this.dataSource.select({
      from: 0,
      to: 10000,
      sorting: { field: 'name', direction: SortDirection.Ascending }
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: data => {
        this.totalCount = data.total;
        const grouped = groupBy(data.data, 'primaryId');
        this.selections = {};
        const rawGroups = map(grouped, (items, primaryId) => {
          this.selections[primaryId] = new SelectionModel<ContactListItem>(true, []);

          const sortedItems = sortBy(items, item => item.primary ? 0 : 1);
          const primaryItem = sortedItems.find(item => item.primary);
          const duplicateItem = sortedItems.find(item => !item.primary);

          if (primaryItem && duplicateItem) {
            this.selections[primaryId].select(primaryItem, duplicateItem);
          } else if (sortedItems.length >= 2) {
            this.selections[primaryId].select(sortedItems[0], sortedItems[1]);
          } else {
            sortedItems.forEach(item => this.selections[primaryId].select(item));
          }
          const name = primaryItem?.name || sortedItems[0]?.name || '';
          const rawMatchReasons = sortedItems.find(item => item.matchReasons)?.matchReasons || sortedItems[0]?.matchReasons || '';
          const matchReasons = this.formatMatchReasons(rawMatchReasons);
          return { name, primaryId, matchReasons, items: sortedItems };
        });

        // Sort duplicate groups alphabetically A -> Z by primary contact display name
        this.groups = rawGroups.sort((a, b) => {
          const nameA = (a.name || '').trim().toLowerCase();
          const nameB = (b.name || '').trim().toLowerCase();
          if (!nameA && !nameB) return 0;
          if (!nameA) return 1;
          if (!nameB) return -1;
          return nameA.localeCompare(nameB);
        });

        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.notificationService.processAll(error);
      }
    });
  }

  formatMatchReasons(rawReasons?: string): string {
    if (!rawReasons) return '';
    const orderMap: { [key: string]: number } = {
      'Business Name': 1,
      'Email': 2,
      'Other Emails': 3,
      'Phone 1': 4,
      'Phone 2': 5,
      'Phone': 6,
      'First Name + Last Name': 7,
      'Addressee': 8,
    };
    const parts = rawReasons.split(',').map(s => {
      const trimmed = s.trim();
      if (trimmed === 'First Name + Surname') {
        return 'First Name + Last Name';
      }
      return trimmed;
    }).filter(Boolean);

    const uniqueParts = Array.from(new Set(parts));
    uniqueParts.sort((a, b) => (orderMap[a] || 99) - (orderMap[b] || 99));
    return uniqueParts.join(', ');
  }

  onEdit(contact: ContactListItem): void {
    if (this.userContext.hasPermission(BuilderPermissionSet.Contacts.Details.ANY)) {
      this.router.navigateToChild(['/contacts', contact.id], {
        parentTitle: 'lbl_duplicate_contacts',
      });
    }
  }

  isAllSelected(group: { name: string; primaryId: string; items: ContactListItem[] }): boolean {
    const selection = this.selections[group.primaryId];
    if (!selection) return false;
    const numSelected = selection.selected.length;
    const numRows = group.items.length;
    return numSelected === numRows;
  }

  masterToggle(group: { name: string; primaryId: string; items: ContactListItem[] }): void {
    const selection = this.selections[group.primaryId];
    if (!selection) return;
    this.isAllSelected(group) ?
      selection.clear() :
      group.items.forEach(row => selection.select(row));
  }

  getFormattedTypes(contact: ContactListItem): string {
    if (!contact.types) {
      return '';
    }
    return sortBy(
      toPairs(contact.types)
        .filter(([_, active]) => active === '1')
        .map(([type]) => this.contactTypes ? this.contactTypes.get(<ContactType>type) : type),
      (t) => t
    ).join(', ');
  }

  isCompareEnabled(primaryId: string): boolean {
    const selection = this.selections[primaryId];
    return !!(selection && selection.selected.length === 2);
  }

  toggleSelection(primaryId: string, element: ContactListItem): void {
    const selection = this.selections[primaryId];
    if (!selection) return;

    if (selection.isSelected(element)) {
      selection.deselect(element);
    } else {
      if (selection.selected.length >= 2) {
        selection.deselect(selection.selected[0]);
      }
      selection.select(element);
    }
  }
}
